import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ROUTE_PATHS, GRAVE_THEMES, GRAVE_SKINS, DECO_ELEMENTS,
  type GraveElement, type GraveTheme, type DecoElement
} from "@/lib/index";
import { springPresets } from "@/lib/motion";
import {
  Layers, Type, Image, Upload, Palette, Crown,
  Trash2, Copy, ChevronDown, ChevronUp, Eye, Save,
  RotateCcw, X, Check, Info
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/* ─── pomocnicze ───────────────────────────────────────────── */
type TabId = "theme" | "elements" | "text" | "photo" | "layers";
type CategoryFilter = "all" | "cross" | "flower" | "candle" | "frame" | "plaque" | "statue";

let elCounter = 0;
const newId = () => `el_${++elCounter}_${Date.now()}`;

const CANVAS_W = 420;
const CANVAS_H = 560;

/* ─── komponent jednego elementu na płótnie ─────────────────── */
function CanvasItem({
  el, selected, onSelect, onChange, onDelete,
}: {
  el: GraveElement;
  selected: boolean;
  onSelect: (id: string) => void;
  onChange: (updated: GraveElement) => void;
  onDelete: (id: string) => void;
}) {
  const dragStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const resizeStart = useRef<{ mx: number; my: number; ow: number; oh: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(el.id);
    dragStart.current = { mx: e.clientX, my: e.clientY, ox: el.x, oy: el.y };
    const onMove = (me: MouseEvent) => {
      if (!dragStart.current) return;
      const dx = me.clientX - dragStart.current.mx;
      const dy = me.clientY - dragStart.current.my;
      const nx = Math.max(0, Math.min(CANVAS_W - el.width, dragStart.current.ox + dx));
      const ny = Math.max(0, Math.min(CANVAS_H - el.height, dragStart.current.oy + dy));
      onChange({ ...el, x: nx, y: ny });
    };
    const onUp = () => {
      dragStart.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleResize = (e: React.MouseEvent) => {
    e.stopPropagation();
    resizeStart.current = { mx: e.clientX, my: e.clientY, ow: el.width, oh: el.height };
    const onMove = (me: MouseEvent) => {
      if (!resizeStart.current) return;
      const dx = me.clientX - resizeStart.current.mx;
      const dy = me.clientY - resizeStart.current.my;
      onChange({
        ...el,
        width: Math.max(30, resizeStart.current.ow + dx),
        height: Math.max(30, resizeStart.current.oh + dy),
      });
    };
    const onUp = () => {
      resizeStart.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      style={{
        position: "absolute",
        left: el.x,
        top: el.y,
        width: el.width,
        height: el.height,
        zIndex: el.zIndex,
        cursor: "move",
        outline: selected ? "1.5px solid rgba(212,175,55,0.7)" : "none",
        borderRadius: 4,
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Zawartość */}
      {el.type === "text" ? (
        <div
          className="w-full h-full flex items-center justify-center text-center leading-snug px-1 overflow-hidden"
          style={{
            fontSize: el.fontSize || 14,
            color: el.color || "rgba(220,230,245,0.9)",
            fontFamily: "'Playfair Display', serif",
            fontStyle: "italic",
            textShadow: "0 1px 4px rgba(0,0,0,0.6)",
          }}
        >
          {el.textContent || "Tekst"}
        </div>
      ) : el.type === "photo" ? (
        <div
          className="w-full h-full rounded overflow-hidden flex items-center justify-center"
          style={{ border: "1px solid rgba(212,175,55,0.25)", background: "oklch(0.12 0.02 220)" }}
        >
          {el.textContent ? (
            <img src={el.textContent} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center">
              <Image className="w-6 h-6 mx-auto mb-1" style={{ color: "rgba(140,155,175,0.4)" }} />
              <p className="text-xs" style={{ color: "rgba(120,135,155,0.5)" }}>Zdjęcie</p>
            </div>
          )}
        </div>
      ) : el.imagePath ? (
        <img
          src={el.imagePath}
          alt={el.label}
          className="w-full h-full object-contain"
          draggable={false}
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }}
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center text-4xl"
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }}
        >
          {el.label}
        </div>
      )}

      {/* Kontrolki zaznaczenia */}
      {selected && (
        <>
          {/* Usuń */}
          <button
            className="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center z-50"
            style={{ background: "rgba(180,50,50,0.85)", color: "white" }}
            onMouseDown={(e) => { e.stopPropagation(); onDelete(el.id); }}
          >
            <X className="w-3 h-3" />
          </button>
          {/* Resize */}
          <div
            className="absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-sm cursor-se-resize z-50"
            style={{ background: "rgba(212,175,55,0.8)", border: "1px solid rgba(212,175,55,0.4)" }}
            onMouseDown={handleResize}
          />
        </>
      )}
    </div>
  );
}

/* ─── główny kreator ─────────────────────────────────────────── */
export default function GraveCreator() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plotId = params.get("plot") || "empty1";
  const initPlan = params.get("plan") || "Podstawowy";

  /* — dane nagrobka — */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [deathYear, setDeathYear] = useState("");
  const [epitaph, setEpitaph]     = useState("");

  /* — wygląd — */
  const [selectedTheme, setSelectedTheme] = useState<GraveTheme>(GRAVE_THEMES[0]);
  const [selectedSkin, setSelectedSkin]   = useState<string>("minimal");
  const [elements, setElements]           = useState<GraveElement[]>([]);
  const [selectedElId, setSelectedElId]   = useState<string | null>(null);

  /* — UI — */
  const [activeTab, setActiveTab]         = useState<TabId>("theme");
  const [catFilter, setCatFilter]         = useState<CategoryFilter>("all");
  const [saved, setSaved]                 = useState(false);
  const [publishing, setPublishing]       = useState(false);
  const [published, setPublished]         = useState(false);
  const [saveError, setSaveError]         = useState<string | null>(null);
  const [graveDbId, setGraveDbId]         = useState<string | null>(null);
  const [previewMode, setPreviewMode]     = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [pendingPremium, setPendingPremium] = useState<string>("");

  const { user } = useAuth();

  /* — tekst panel — */
  const [newText, setNewText]   = useState("Napis");
  const [fontSize, setFontSize] = useState(16);
  const [textColor, setTextColor] = useState("rgba(220,230,245,0.9)");

  const canvasRef = useRef<HTMLDivElement>(null);
  const fileRef   = useRef<HTMLInputElement>(null);

  const isPremiumUser = initPlan === "Premium";

  /* Dodaj element dekoracyjny */
  const addDecoElement = (deco: DecoElement) => {
    if (deco.isPremium && !isPremiumUser) {
      setPendingPremium(deco.label);
      setShowPremiumModal(true);
      return;
    }
    const el: GraveElement = {
      id: newId(),
      type: deco.category as GraveElement["type"],
      label: deco.emoji || deco.label,
      image: deco.imagePath,
      imagePath: deco.imagePath,
      x: Math.random() * (CANVAS_W - deco.defaultWidth - 20) + 10,
      y: Math.random() * (CANVAS_H - deco.defaultHeight - 80) + 40,
      width: deco.defaultWidth,
      height: deco.defaultHeight,
      zIndex: elements.length + 1,
    };
    setElements((prev) => [...prev, el]);
    setSelectedElId(el.id);
  };

  /* Dodaj tekst */
  const addText = () => {
    const el: GraveElement = {
      id: newId(), type: "text",
      label: newText, textContent: newText,
      x: 60, y: CANVAS_H / 2 - 20,
      width: 300, height: 40,
      zIndex: elements.length + 1,
      fontSize, color: textColor,
    };
    setElements((prev) => [...prev, el]);
    setSelectedElId(el.id);
    setNewText("Napis");
  };

  /* Upload zdjęcia */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const el: GraveElement = {
      id: newId(), type: "photo",
      label: "📷", textContent: url,
      x: CANVAS_W / 2 - 60, y: CANVAS_H / 2 - 60,
      width: 120, height: 120,
      zIndex: elements.length + 1,
    };
    setElements((prev) => [...prev, el]);
    setSelectedElId(el.id);
  };

  const changeTheme = (theme: GraveTheme) => {
    if (theme.isPremium && !isPremiumUser) {
      setPendingPremium(theme.name);
      setShowPremiumModal(true);
      return;
    }
    setSelectedTheme(theme);
  };

  const deleteElement = useCallback((id: string) => {
    setElements((prev) => prev.filter((e) => e.id !== id));
    setSelectedElId(null);
  }, []);

  const updateElement = useCallback((updated: GraveElement) => {
    setElements((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  }, []);

  const bringForward = (id: string) => {
    setElements((prev) => prev.map((e) => e.id === id ? { ...e, zIndex: e.zIndex + 1 } : e));
  };
  const sendBackward = (id: string) => {
    setElements((prev) => prev.map((e) => e.id === id ? { ...e, zIndex: Math.max(0, e.zIndex - 1) } : e));
  };
  const duplicateElement = (id: string) => {
    const src = elements.find((e) => e.id === id);
    if (!src) return;
    const dup = { ...src, id: newId(), x: src.x + 12, y: src.y + 12, zIndex: elements.length + 1 };
    setElements((prev) => [...prev, dup]);
    setSelectedElId(dup.id);
  };

  /* Parsowanie plot_id → row/col */
  const parsePlotCoords = () => {
    // format: "empty-ROW-COL" lub "gN" (zajęte)
    const m = plotId.match(/^empty-(\d+)-(\d+)$/);
    if (m) return { row: parseInt(m[1]), col: parseInt(m[2]) };
    // fallback: jeśli plotId to liczba globalna (1-100)
    const num = parseInt(plotId);
    if (!isNaN(num) && num >= 1 && num <= 100) {
      const idx = num - 1;
      return { row: Math.floor(idx / 5), col: idx % 5 };
    }
    return { row: 0, col: 0 };
  };

  /* Zapis roboczy do Supabase */
  const handleSave = async () => {
    if (!user) { setSaveError("Musisz być zalogowany, aby zapisać grób."); return; }
    setSaveError(null);
    const { row, col } = parsePlotCoords();
    const payload = {
      user_id:    user.id,
      plot_id:    plotId,
      plot_row:   row,
      plot_col:   col,
      plan:       initPlan.toLowerCase(),
      first_name: firstName.trim(),
      last_name:  lastName.trim(),
      birth_year: birthYear.trim(),
      death_year: deathYear.trim(),
      epitaph:    epitaph.trim(),
      theme_id:   selectedTheme.id,
      skin_id:    selectedSkin,
      elements:   elements,
      is_published: published,
    };

    let result;
    if (graveDbId) {
      result = await supabase.from("graves").update(payload).eq("id", graveDbId).select().single();
    } else {
      result = await supabase.from("graves").upsert(payload, { onConflict: "plot_row,plot_col" }).select().single();
    }

    if (result.error) {
      setSaveError(result.error.message);
    } else {
      setGraveDbId(result.data.id);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  /* Publikacja grobu na placu */
  const handlePublish = async () => {
    if (!user) { setSaveError("Musisz być zalogowany."); return; }
    if (!firstName.trim() || !lastName.trim()) {
      setSaveError("Uzupełnij imię i nazwisko przed publikacją.");
      return;
    }
    setPublishing(true);
    setSaveError(null);
    const { row, col } = parsePlotCoords();
    const payload = {
      user_id:      user.id,
      plot_id:      plotId,
      plot_row:     row,
      plot_col:     col,
      plan:         initPlan.toLowerCase(),
      first_name:   firstName.trim(),
      last_name:    lastName.trim(),
      birth_year:   birthYear.trim(),
      death_year:   deathYear.trim(),
      epitaph:      epitaph.trim(),
      theme_id:     selectedTheme.id,
      skin_id:      selectedSkin,
      elements:     elements,
      is_published: true,
    };

    let result;
    if (graveDbId) {
      result = await supabase.from("graves").update(payload).eq("id", graveDbId).select().single();
    } else {
      result = await supabase.from("graves").upsert(payload, { onConflict: "plot_row,plot_col" }).select().single();
    }

    setPublishing(false);
    if (result.error) {
      setSaveError(result.error.message);
    } else {
      setGraveDbId(result.data.id);
      setPublished(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  /* Odczyt istniejącego grobu przy montowaniu */
  useEffect(() => {
    const loadExisting = async () => {
      if (!user) return;
      const { row, col } = (() => {
        const m = plotId.match(/^empty-(\d+)-(\d+)$/);
        if (m) return { row: parseInt(m[1]), col: parseInt(m[2]) };
        const num = parseInt(plotId);
        if (!isNaN(num) && num >= 1) { const i = num-1; return { row: Math.floor(i/5), col: i%5 }; }
        return { row: 0, col: 0 };
      })();
      const { data } = await supabase
        .from("graves")
        .select("*")
        .eq("user_id", user.id)
        .eq("plot_row", row)
        .eq("plot_col", col)
        .maybeSingle();
      if (data) {
        setGraveDbId(data.id);
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setBirthYear(data.birth_year || "");
        setDeathYear(data.death_year || "");
        setEpitaph(data.epitaph || "");
        setSelectedSkin(data.skin_id || "minimal");
        const t = GRAVE_THEMES.find(t => t.id === data.theme_id);
        if (t) setSelectedTheme(t);
        if (Array.isArray(data.elements)) setElements(data.elements as GraveElement[]);
        setPublished(data.is_published || false);
      }
    };
    loadExisting();
  }, [user, plotId]);

  const selectedEl = elements.find((e) => e.id === selectedElId);

  const filteredDecos = DECO_ELEMENTS.filter(
    (d) => catFilter === "all" || d.category === catFilter
  );

  const TABS: { id: TabId; icon: React.ReactNode; label: string }[] = [
    { id: "theme",    icon: <Palette className="w-4 h-4" />,  label: "Motyw" },
    { id: "elements", icon: <Layers className="w-4 h-4" />,   label: "Elementy" },
    { id: "text",     icon: <Type className="w-4 h-4" />,     label: "Tekst" },
    { id: "photo",    icon: <Image className="w-4 h-4" />,    label: "Zdjęcia" },
    { id: "layers",   icon: <Layers className="w-4 h-4" />,   label: "Warstwy" },
  ];

  const CAT_LABELS: Record<CategoryFilter, string> = {
    all: "Wszystkie", cross: "Krzyże", flower: "Kwiaty",
    candle: "Znicze", frame: "Ramy", plaque: "Tablice", statue: "Rzeźby",
  };

  return (
    <Layout>
      <div
        className="min-h-screen flex flex-col"
        style={{ background: "oklch(0.07 0.015 230)" }}
      >
        {/* ── Nagłówek kreatora ── */}
        <div
          className="sticky top-20 z-30 flex items-center justify-between px-4 py-3"
          style={{
            background: "rgba(8,10,20,0.92)",
            borderBottom: "1px solid oklch(0.16 0.025 220)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(ROUTE_PATHS.CMENTARZ)}
              className="text-sm flex items-center gap-1.5 transition-opacity hover:opacity-70"
              style={{ color: "rgba(140,155,175,0.65)" }}
            >
              ← Plac
            </button>
            <div className="w-px h-4" style={{ background: "oklch(0.22 0.03 220)" }} />
            <span className="text-sm font-medium" style={{ color: "rgba(212,175,55,0.75)", fontFamily: "'Playfair Display', serif" }}>
              Kreator e-grobu
            </span>
            <Badge
              variant="outline"
              className="text-xs border-0"
              style={{ background: "oklch(0.12 0.02 220)", color: "rgba(140,155,175,0.6)" }}
            >
              #{plotId}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
              style={{
                background: previewMode ? "rgba(212,175,55,0.15)" : "oklch(0.12 0.02 220)",
                color: previewMode ? "rgba(212,175,55,0.8)" : "rgba(140,155,175,0.7)",
                border: `1px solid ${previewMode ? "rgba(212,175,55,0.3)" : "oklch(0.20 0.03 220)"}`,
              }}
            >
              <Eye className="w-3.5 h-3.5" /> Podgląd
            </button>
            {/* Błąd zapisu */}
            {saveError && (
              <span className="text-xs px-2" style={{ color: "rgba(220,80,80,0.85)" }}>
                ⚠ {saveError}
              </span>
            )}

            {/* Zapisz roboczy */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              transition={springPresets.snappy}
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                background: saved ? "rgba(80,180,100,0.3)" : "oklch(0.14 0.025 220)",
                color: saved ? "rgba(80,220,120,0.9)" : "rgba(170,185,205,0.85)",
                border: `1px solid ${saved ? "rgba(80,180,100,0.4)" : "oklch(0.22 0.03 220)"}`,
              }}
            >
              {saved ? <><Check className="w-3.5 h-3.5" /> Zapisano</> : <><Save className="w-3.5 h-3.5" /> Zapisz</>}
            </motion.button>

            {/* Pochowaj mnie — publikacja na placu */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              transition={springPresets.snappy}
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                background: published
                  ? "rgba(80,180,100,0.25)"
                  : publishing
                  ? "rgba(212,175,55,0.4)"
                  : "rgba(212,175,55,0.85)",
                color: published ? "rgba(80,220,120,0.9)" : "rgba(8,9,18,0.95)",
                opacity: publishing ? 0.7 : 1,
                cursor: publishing ? "wait" : "pointer",
              }}
            >
              {publishing ? (
                <><Save className="w-3.5 h-3.5 animate-spin" /> Publikowanie…</>
              ) : published ? (
                <><Check className="w-3.5 h-3.5" /> Opublikowano ✦</>
              ) : (
                <>✦ Pochowaj mnie</>
              )}
            </motion.button>
          </div>
        </div>

        {/* ── Główny obszar ── */}
        <div className="flex flex-1 overflow-hidden">
          {/* ── Panel boczny lewy — dane ── */}
          {!previewMode && (
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={springPresets.gentle}
              className="w-64 flex-shrink-0 overflow-y-auto py-6 px-4 space-y-4"
              style={{ borderRight: "1px solid oklch(0.14 0.02 220)", background: "oklch(0.075 0.015 225)" }}
            >
              <p className="text-xs uppercase tracking-widest" style={{ color: "rgba(120,135,155,0.55)" }}>
                Dane nagrobka
              </p>

              {[
                { label: "Imię", val: firstName, set: setFirstName, ph: "Jan" },
                { label: "Nazwisko", val: lastName, set: setLastName, ph: "Kowalski" },
                { label: "Rok urodzenia", val: birthYear, set: setBirthYear, ph: "1942" },
                { label: "Rok śmierci", val: deathYear, set: setDeathYear, ph: "2024" },
              ].map(({ label, val, set, ph }) => (
                <div key={label}>
                  <label className="block text-xs mb-1" style={{ color: "rgba(130,145,165,0.65)" }}>
                    {label}
                  </label>
                  <Input
                    placeholder={ph}
                    value={val}
                    onChange={(e) => set(e.target.value)}
                    className="h-9 text-sm border-0 focus-visible:ring-1 focus-visible:ring-primary/40 rounded-lg"
                    style={{ background: "oklch(0.11 0.02 220)", color: "rgba(205,220,238,0.9)" }}
                  />
                </div>
              ))}

              <div>
                <label className="block text-xs mb-1" style={{ color: "rgba(130,145,165,0.65)" }}>
                  Epitafium
                </label>
                <textarea
                  placeholder="Spokój wieczny..."
                  value={epitaph}
                  onChange={(e) => setEpitaph(e.target.value)}
                  rows={3}
                  className="w-full text-sm rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary/40"
                  style={{
                    background: "oklch(0.11 0.02 220)",
                    color: "rgba(205,220,238,0.9)",
                    border: "none",
                    fontSize: 13,
                  }}
                />
              </div>

              {/* Separator */}
              <div className="h-px" style={{ background: "oklch(0.16 0.025 220)" }} />

              {/* Upload pliku */}
              <p className="text-xs uppercase tracking-widest" style={{ color: "rgba(120,135,155,0.55)" }}>
                Pliki & Zdjęcia
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full py-2.5 rounded-lg text-xs flex items-center justify-center gap-2 transition-all hover:brightness-110"
                style={{
                  border: "1px dashed rgba(212,175,55,0.3)",
                  color: "rgba(212,175,55,0.65)",
                  background: "oklch(0.09 0.02 45 / 0.15)",
                }}
              >
                <Upload className="w-3.5 h-3.5" /> Dodaj zdjęcie
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
              <p className="text-xs" style={{ color: "rgba(110,125,145,0.5)" }}>
                PNG, JPG, WEBP · maks. 10 MB
              </p>

              {/* Plan info */}
              <div
                className="rounded-xl p-3 mt-2"
                style={{
                  background: "oklch(0.10 0.025 45 / 0.2)",
                  border: "1px solid rgba(212,175,55,0.15)",
                }}
              >
                <p className="text-xs font-medium mb-0.5" style={{ color: "rgba(212,175,55,0.7)" }}>
                  Plan: {initPlan}
                </p>
                <p className="text-xs" style={{ color: "rgba(120,135,155,0.55)" }}>
                  {initPlan === "Premium" ? "Pełny dostęp do wszystkich elementów" :
                   initPlan === "Podstawowy" ? "Elementy podstawowe + wybrane premium" :
                   "Elementy darmowe"}
                </p>
              </div>
            </motion.div>
          )}

          {/* ── Płótno ── */}
          <div className="flex-1 flex items-start justify-center py-8 px-4 overflow-auto">
            <div className="relative">
              {/* Płótno nagrobka */}
              <div
                ref={canvasRef}
                className="relative overflow-hidden select-none"
                style={{
                  width: CANVAS_W,
                  height: CANVAS_H,
                  background: selectedTheme.bgColor,
                  borderRadius: 12,
                  boxShadow: "0 0 60px rgba(0,0,0,0.7), 0 0 120px rgba(0,0,0,0.4)",
                  border: "1px solid rgba(212,175,55,0.15)",
                  cursor: "default",
                }}
                onClick={() => setSelectedElId(null)}
              >
                {/* Tekstura tła */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.015) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(212,175,55,0.04) 0%, transparent 40%)",
                    zIndex: 0,
                  }}
                />

                {/* Wbudowany nagrobek (tło) */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
                  style={{ top: 30, zIndex: 1 }}
                >
                  {/* Krzyż dekoracyjny */}
                  <div className="flex flex-col items-center opacity-20">
                    <div className="w-px h-16 rounded" style={{ background: selectedTheme.stoneColor }} />
                    <div className="w-10 h-px rounded -mt-10" style={{ background: selectedTheme.stoneColor }} />
                  </div>
                </div>

                {/* Tablica inskrypcji (wbudowana) */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 pointer-events-none text-center"
                  style={{ bottom: 50, zIndex: 1, width: 300 }}
                >
                  <div
                    className="px-6 py-4 rounded"
                    style={{
                      background: `${selectedTheme.stoneColor}22`,
                      border: `1px solid ${selectedTheme.stoneColor}33`,
                    }}
                  >
                    <p
                      className="text-lg font-semibold mb-1"
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        color: `${selectedTheme.stoneColor}dd`,
                        letterSpacing: "0.06em",
                      }}
                    >
                      {firstName || "Imię"} {lastName || "Nazwisko"}
                    </p>
                    {(birthYear || deathYear) && (
                      <p className="text-sm mb-1" style={{ color: `${selectedTheme.stoneColor}99` }}>
                        {birthYear || "?"} – {deathYear || "?"}
                      </p>
                    )}
                    {epitaph && (
                      <p
                        className="text-xs italic mt-1"
                        style={{
                          color: `${selectedTheme.stoneColor}88`,
                          fontFamily: "'Playfair Display', serif",
                        }}
                      >
                        „{epitaph}"
                      </p>
                    )}
                  </div>
                </div>

                {/* Elementy użytkownika */}
                {elements.map((el) => (
                  <CanvasItem
                    key={el.id}
                    el={el}
                    selected={selectedElId === el.id && !previewMode}
                    onSelect={setSelectedElId}
                    onChange={updateElement}
                    onDelete={deleteElement}
                  />
                ))}

                {/* Siatka pomocnicza (tylko w trybie edycji) */}
                {!previewMode && (
                  <div
                    className="absolute inset-0 pointer-events-none opacity-[0.03]"
                    style={{
                      backgroundImage: "linear-gradient(rgba(212,175,55,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,1) 1px, transparent 1px)",
                      backgroundSize: "40px 40px",
                      zIndex: 0,
                    }}
                  />
                )}
              </div>

              {/* Opis pod płótnem */}
              {!previewMode && (
                <p className="text-center mt-3 text-xs" style={{ color: "rgba(110,125,145,0.45)" }}>
                  Klikaj elementy, by je zaznaczać · przeciągaj, by przesuwać · ↘ narożnik, by zmieniać rozmiar
                </p>
              )}
            </div>
          </div>

          {/* ── Panel boczny prawy — narzędzia ── */}
          {!previewMode && (
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={springPresets.gentle}
              className="w-72 flex-shrink-0 flex flex-col"
              style={{ borderLeft: "1px solid oklch(0.14 0.02 220)", background: "oklch(0.075 0.015 225)" }}
            >
              {/* Zakładki */}
              <div
                className="flex border-b"
                style={{ borderColor: "oklch(0.14 0.02 220)" }}
              >
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-1 flex flex-col items-center gap-1 py-3 text-xs transition-all"
                    style={{
                      color: activeTab === tab.id ? "rgba(212,175,55,0.85)" : "rgba(110,125,145,0.55)",
                      borderBottom: activeTab === tab.id ? "2px solid rgba(212,175,55,0.6)" : "2px solid transparent",
                    }}
                  >
                    {tab.icon}
                    <span className="hidden sm:block">{tab.label}</span>
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* ── Motyw ── */}
                {activeTab === "theme" && (
                  <>
                    <p className="text-xs uppercase tracking-widest" style={{ color: "rgba(120,135,155,0.55)" }}>
                      Kolor kamienia
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {GRAVE_THEMES.map((theme) => (
                        <button
                          key={theme.id}
                          onClick={() => changeTheme(theme)}
                          className="relative rounded-xl p-3 text-left transition-all"
                          style={{
                            background: theme.bgColor,
                            border: selectedTheme.id === theme.id
                              ? "2px solid rgba(212,175,55,0.7)"
                              : "1px solid rgba(212,175,55,0.12)",
                            opacity: theme.isPremium && !isPremiumUser ? 0.7 : 1,
                          }}
                        >
                          {theme.isPremium && !isPremiumUser && (
                            <Crown className="absolute top-1.5 right-1.5 w-3 h-3" style={{ color: "rgba(212,175,55,0.7)" }} />
                          )}
                          <div
                            className="w-full h-6 rounded-md mb-2"
                            style={{ background: theme.stoneColor + "88" }}
                          />
                          <p className="text-xs font-medium" style={{ color: "rgba(200,215,232,0.85)" }}>
                            {theme.name}
                          </p>
                        </button>
                      ))}
                    </div>

                    <div className="h-px" style={{ background: "oklch(0.16 0.025 220)" }} />

                    <p className="text-xs uppercase tracking-widest" style={{ color: "rgba(120,135,155,0.55)" }}>
                      Szablony
                    </p>
                    <div className="space-y-2">
                      {GRAVE_SKINS.map((skin) => (
                        <button
                          key={skin.id}
                          onClick={() => {
                            if (skin.isPremium && !isPremiumUser) {
                              setPendingPremium(skin.name);
                              setShowPremiumModal(true);
                              return;
                            }
                            setSelectedSkin(skin.id);
                            const t = GRAVE_THEMES.find((t) => t.id === skin.themeId);
                            if (t) setSelectedTheme(t);
                          }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                          style={{
                            background: selectedSkin === skin.id ? "oklch(0.14 0.03 45 / 0.3)" : "oklch(0.10 0.02 220)",
                            border: `1px solid ${selectedSkin === skin.id ? "rgba(212,175,55,0.35)" : "oklch(0.18 0.025 220)"}`,
                          }}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
                            style={{ background: "oklch(0.08 0.02 220)" }}
                          >
                            {skin.isPremium ? (
                              <Crown className="w-4 h-4" style={{ color: "rgba(212,175,55,0.65)" }} />
                            ) : (
                              <Layers className="w-4 h-4" style={{ color: "rgba(140,155,175,0.5)" }} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate" style={{ color: "rgba(205,218,235,0.85)" }}>
                              {skin.name}
                              {skin.isPremium && !isPremiumUser && (
                                <span className="ml-1.5 text-xs" style={{ color: "rgba(212,175,55,0.6)" }}>
                                  ★ Premium
                                </span>
                              )}
                            </p>
                            <p className="text-xs truncate" style={{ color: "rgba(110,125,145,0.55)" }}>
                              {skin.description}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* ── Elementy dekoracyjne ── */}
                {activeTab === "elements" && (
                  <>
                    {/* Filtry kategorii */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(Object.keys(CAT_LABELS) as CategoryFilter[]).map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setCatFilter(cat)}
                          className="px-2.5 py-1 rounded-full text-xs transition-all"
                          style={{
                            background: catFilter === cat ? "rgba(212,175,55,0.75)" : "oklch(0.12 0.02 220)",
                            color: catFilter === cat ? "rgba(8,9,18,0.95)" : "rgba(130,145,165,0.65)",
                            border: catFilter === cat ? "none" : "1px solid oklch(0.18 0.025 220)",
                          }}
                        >
                          {CAT_LABELS[cat]}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {filteredDecos.map((deco) => (
                        <motion.button
                          key={deco.id}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={springPresets.snappy}
                          onClick={() => addDecoElement(deco)}
                          className="relative flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all"
                          style={{
                            background: "oklch(0.11 0.02 220)",
                            border: "1px solid oklch(0.18 0.025 220)",
                          }}
                        >
                          {deco.isPremium && !isPremiumUser && (
                            <Crown
                              className="absolute top-1 right-1 w-2.5 h-2.5"
                              style={{ color: "rgba(212,175,55,0.65)" }}
                            />
                          )}
                          {deco.imagePath ? (
                            <img
                              src={deco.imagePath}
                              alt={deco.label}
                              className="w-10 h-10 object-contain"
                              style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.5))" }}
                            />
                          ) : (
                            <span className="text-2xl">{deco.emoji}</span>
                          )}
                          <span className="text-xs text-center leading-tight" style={{ color: "rgba(155,170,190,0.7)", fontSize: 10 }}>
                            {deco.label}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  </>
                )}

                {/* ── Tekst ── */}
                {activeTab === "text" && (
                  <>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: "rgba(130,145,165,0.65)" }}>
                        Treść
                      </label>
                      <Input
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        placeholder="Wpisz tekst..."
                        className="h-9 text-sm border-0 focus-visible:ring-1 focus-visible:ring-primary/40 rounded-lg"
                        style={{ background: "oklch(0.11 0.02 220)", color: "rgba(205,220,238,0.9)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: "rgba(130,145,165,0.65)" }}>
                        Rozmiar: {fontSize}px
                      </label>
                      <input
                        type="range" min={10} max={48} value={fontSize}
                        onChange={(e) => setFontSize(Number(e.target.value))}
                        className="w-full accent-yellow-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-2" style={{ color: "rgba(130,145,165,0.65)" }}>
                        Kolor
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          "rgba(220,230,245,0.9)",
                          "rgba(212,175,55,0.85)",
                          "rgba(180,200,230,0.8)",
                          "rgba(200,180,160,0.85)",
                          "rgba(255,255,255,0.7)",
                          "rgba(160,200,255,0.8)",
                          "rgba(255,180,180,0.7)",
                          "rgba(180,255,180,0.6)",
                        ].map((c) => (
                          <button
                            key={c}
                            onClick={() => setTextColor(c)}
                            className="w-full h-7 rounded-md transition-all"
                            style={{
                              background: c,
                              border: textColor === c ? "2px solid rgba(212,175,55,0.9)" : "1px solid rgba(255,255,255,0.1)",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springPresets.snappy}
                      onClick={addText}
                      className="w-full py-2.5 rounded-xl text-sm font-medium transition-all"
                      style={{
                        background: "rgba(212,175,55,0.75)",
                        color: "rgba(8,9,18,0.95)",
                      }}
                    >
                      + Dodaj tekst
                    </motion.button>
                  </>
                )}

                {/* ── Zdjęcia ── */}
                {activeTab === "photo" && (
                  <>
                    <p className="text-xs" style={{ color: "rgba(140,155,175,0.65)" }}>
                      Dodaj zdjęcia do grobu — pojawią się na płótnie jako elementy, które możesz dowolnie przenosić i skalować.
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springPresets.snappy}
                      onClick={() => fileRef.current?.click()}
                      className="w-full py-8 rounded-xl flex flex-col items-center gap-3 transition-all"
                      style={{
                        border: "2px dashed rgba(212,175,55,0.3)",
                        color: "rgba(212,175,55,0.6)",
                        background: "oklch(0.09 0.02 45 / 0.15)",
                      }}
                    >
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Kliknij lub przeciągnij plik</span>
                      <span className="text-xs" style={{ color: "rgba(120,135,155,0.5)" }}>
                        PNG, JPG, WEBP · maks. 10 MB
                      </span>
                    </motion.button>
                    <div
                      className="rounded-xl p-3"
                      style={{ background: "oklch(0.10 0.02 220)", border: "1px solid oklch(0.16 0.025 220)" }}
                    >
                      <div className="flex items-start gap-2">
                        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "rgba(140,155,175,0.5)" }} />
                        <p className="text-xs" style={{ color: "rgba(120,135,155,0.6)" }}>
                          Pojemność zależy od planu: Free 500 MB, Podstawowy 5 GB, Premium 50 GB.
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* ── Warstwy ── */}
                {activeTab === "layers" && (
                  <>
                    <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(120,135,155,0.55)" }}>
                      Elementy ({elements.length})
                    </p>
                    {elements.length === 0 ? (
                      <p className="text-xs py-4 text-center" style={{ color: "rgba(110,125,145,0.45)" }}>
                        Brak elementów — dodaj coś z zakładki Elementy
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {[...elements].reverse().map((el) => (
                          <div
                            key={el.id}
                            className="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all"
                            style={{
                              background: selectedElId === el.id ? "oklch(0.14 0.03 45 / 0.3)" : "oklch(0.10 0.02 220)",
                              border: `1px solid ${selectedElId === el.id ? "rgba(212,175,55,0.3)" : "oklch(0.17 0.025 220)"}`,
                            }}
                            onClick={() => setSelectedElId(el.id)}
                          >
                            <span className="text-sm w-5 text-center">{el.imagePath ? "🖼" : el.type === "text" ? "T" : el.type === "photo" ? "📷" : el.label}</span>
                            <span className="flex-1 text-xs truncate" style={{ color: "rgba(170,185,205,0.75)" }}>
                              {el.type === "text" ? el.textContent?.slice(0, 20) : el.label}
                            </span>
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); bringForward(el.id); }} title="Do przodu">
                                <ChevronUp className="w-3.5 h-3.5" style={{ color: "rgba(140,155,175,0.5)" }} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); sendBackward(el.id); }} title="Do tyłu">
                                <ChevronDown className="w-3.5 h-3.5" style={{ color: "rgba(140,155,175,0.5)" }} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); duplicateElement(el.id); }} title="Duplikuj">
                                <Copy className="w-3.5 h-3.5" style={{ color: "rgba(140,155,175,0.5)" }} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); deleteElement(el.id); }} title="Usuń">
                                <Trash2 className="w-3.5 h-3.5" style={{ color: "rgba(180,80,80,0.6)" }} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {elements.length > 0 && (
                      <button
                        onClick={() => { setElements([]); setSelectedElId(null); }}
                        className="w-full mt-2 py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 transition-all hover:opacity-80"
                        style={{ border: "1px solid rgba(180,80,80,0.3)", color: "rgba(200,100,100,0.65)" }}
                      >
                        <RotateCcw className="w-3 h-3" /> Wyczyść wszystkie
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Właściwości zaznaczonego elementu */}
              <AnimatePresence>
                {selectedEl && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                    style={{ borderTop: "1px solid oklch(0.14 0.02 220)" }}
                  >
                    <div className="p-3 space-y-2">
                      <p className="text-xs uppercase tracking-widest" style={{ color: "rgba(120,135,155,0.55)" }}>
                        Zaznaczony: {selectedEl.label}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs" style={{ color: "rgba(130,145,165,0.7)" }}>
                        <span>X: {Math.round(selectedEl.x)}</span>
                        <span>Y: {Math.round(selectedEl.y)}</span>
                        <span>W: {Math.round(selectedEl.width)}</span>
                        <span>H: {Math.round(selectedEl.height)}</span>
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={() => bringForward(selectedEl.id)}
                          className="flex-1 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1"
                          style={{ background: "oklch(0.12 0.02 220)", color: "rgba(150,165,185,0.7)", border: "1px solid oklch(0.18 0.025 220)" }}
                        >
                          <ChevronUp className="w-3 h-3" /> Przód
                        </button>
                        <button onClick={() => sendBackward(selectedEl.id)}
                          className="flex-1 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1"
                          style={{ background: "oklch(0.12 0.02 220)", color: "rgba(150,165,185,0.7)", border: "1px solid oklch(0.18 0.025 220)" }}
                        >
                          <ChevronDown className="w-3 h-3" /> Tył
                        </button>
                        <button onClick={() => duplicateElement(selectedEl.id)}
                          className="flex-1 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1"
                          style={{ background: "oklch(0.12 0.02 220)", color: "rgba(150,165,185,0.7)", border: "1px solid oklch(0.18 0.025 220)" }}
                        >
                          <Copy className="w-3 h-3" /> Kopia
                        </button>
                        <button onClick={() => deleteElement(selectedEl.id)}
                          className="py-1.5 px-2.5 rounded-lg text-xs flex items-center justify-center"
                          style={{ background: "rgba(180,50,50,0.15)", color: "rgba(200,100,100,0.7)", border: "1px solid rgba(180,50,50,0.25)" }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>

      {/* Modal premium */}
      <AnimatePresence>
        {showPremiumModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
            onClick={() => setShowPremiumModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={springPresets.snappy}
              className="rounded-2xl p-8 max-w-sm w-full text-center"
              style={{
                background: "oklch(0.10 0.025 215)",
                border: "1px solid rgba(212,175,55,0.2)",
                boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Crown className="w-10 h-10 mx-auto mb-4" style={{ color: "rgba(212,175,55,0.75)" }} />
              <h3
                className="text-xl font-bold mb-2"
                style={{ fontFamily: "'Playfair Display', serif", color: "rgba(220,230,245,0.9)" }}
              >
                Element Premium
              </h3>
              <p className="text-sm mb-2" style={{ color: "rgba(160,175,195,0.75)" }}>
                <span style={{ color: "rgba(212,175,55,0.75)" }}>{pendingPremium}</span>
                {" "}dostępny jest w planie Premium.
              </p>
              <p className="text-xs mb-6" style={{ color: "rgba(120,135,155,0.6)" }}>
                Uaktualnij swój plan, by odblokować wszystkie skórki, szablony i elementy premium.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm transition-all hover:bg-white/5"
                  style={{ border: "1px solid oklch(0.22 0.03 220)", color: "rgba(150,165,185,0.7)" }}
                >
                  Nie teraz
                </button>
                <button
                  onClick={() => { setShowPremiumModal(false); navigate(ROUTE_PATHS.HOME + "#plany"); }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:brightness-110"
                  style={{ background: "rgba(212,175,55,0.78)", color: "rgba(8,9,18,0.95)" }}
                >
                  Zobacz plany ★
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
